<script setup lang="ts">
  import { Floating, Spinner, SpinnerSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useVirtualList, type UseVerticalVirtualListOptions } from '@vueuse/core';
  import { computed, type ComponentPublicInstance, type Ref } from 'vue';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import type { Country, Destination, Hotel, Resort } from '@/shared/model';
  import DestinationOption from './DestinationOption.vue';
  import {
    DROPDOWN_HEADER_ROW_HEIGHT,
    DROPDOWN_OPTION_ROW_HEIGHT,
    DROPDOWN_ROW_HEIGHT_STYLE,
  } from '../config/dropdown';
  import { DESTINATION_TEXTS } from '../config/texts';
  import { destinationEntity } from '../lib/destinationEntity';
  import {
    getSelectedHotels,
    getSelectedResorts,
    getSelectedCountry,
    isCountry,
    type Selection,
  } from '../model/selection';
  import type { DropdownRow, OptionGroup } from '../model/types';

  interface DestinationDropdownProps {
    options: OptionGroup[];
    selection: Selection;
    loading: boolean;
    showSelectAll: boolean;
    selectAllChecked: boolean;
    selectAllCountry?: Country | null;
    visible: boolean;
  }

  const props = withDefaults(defineProps<DestinationDropdownProps>(), {
    selectAllCountry: null,
  });

  interface DestinationDropdownEmits {
    (e: 'select', option: Destination): void;
    (e: 'select-all'): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DestinationDropdownEmits>();

  const rows = computed<DropdownRow[]>(() => {
    const result: DropdownRow[] = [];
    props.options.forEach((group) => {
      if (group.title) {
        result.push({ rowType: 'header', key: `header-${group.kind}-${group.title}`, title: group.title });
      }
      group.items.forEach((item, index) => {
        result.push({
          rowType: 'option',
          key: `option-${group.kind}-${destinationEntity(item).id}`,
          item,
          kind: group.kind,
          lastOfGroup: index === group.items.length - 1,
        });
      });
    });
    return result;
  });

  const virtualListOptions: UseVerticalVirtualListOptions = {
    itemHeight: (index) =>
      rows.value[index]?.rowType === 'header' ? DROPDOWN_HEADER_ROW_HEIGHT : DROPDOWN_OPTION_ROW_HEIGHT,
    overscan: 8,
  };

  const { list, containerProps, wrapperProps } = useVirtualList(rows, virtualListOptions);

  // Vue 2.7: объектный v-bind не применяет ref — привязываем функционально.
  const scrollerRef = containerProps.ref as Ref<HTMLElement | null>;
  const setScrollerRef = (element: Element | ComponentPublicInstance | null): void => {
    scrollerRef.value = element instanceof HTMLElement ? element : null;
  };

  const hasItems = (groups: OptionGroup[]): boolean => {
    return groups.some((group) => group.items.length > 0);
  };

  const isItemChecked = (item: Destination): boolean => {
    if (item.type === 'resort') {
      return getSelectedResorts(props.selection).some((resort: Resort) => resort.id === item.resort.id);
    }
    if (item.type === 'hotel') {
      return getSelectedHotels(props.selection).some((hotel: Hotel) => hotel.id === item.hotel.id);
    }
    return isCountry(props.selection) && getSelectedCountry(props.selection)?.id === item.country.id;
  };

  const onFloatingHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );
</script>

<template>
  <Floating
    class="destination-dropdown__floating"
    :visible="visible"
    :disable-flip="true"
    :teleported="false"
    full-width
    @hide="onFloatingHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div
        class="destination-dropdown"
        data-testid="destination-dropdown"
        :style="DROPDOWN_ROW_HEIGHT_STYLE"
      >
        <DestinationOption
          v-if="showSelectAll && selectAllCountry"
          kind="selectAll"
          data-testid="destination-select-all"
          :option="selectAllCountry"
          :checked="selectAllChecked"
          @toggle="emit('select-all')"
        />

        <div
          v-if="loading"
          class="destination-dropdown__loading"
          data-testid="destination-loading"
        >
          <Spinner :size="SpinnerSize.S" />
        </div>

        <div
          v-else-if="!hasItems(options)"
          class="destination-dropdown__empty"
          data-testid="destination-nothing-found"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ DESTINATION_TEXTS.nothingFound }}
          </Typo>
        </div>

        <div
          v-else
          :ref="setScrollerRef"
          class="destination-dropdown__scroller"
          :style="containerProps.style"
          @scroll="containerProps.onScroll"
        >
          <div v-bind="wrapperProps">
            <template v-for="virtualRow in list">
              <Typo
                v-if="virtualRow.data.rowType === 'header'"
                :key="virtualRow.data.key"
                class="destination-dropdown__group-header"
                data-testid="destination-group-header"
                :variant="TypoVariant.BodyLMedium"
              >
                {{ virtualRow.data.title }}
              </Typo>

              <DestinationOption
                v-else
                :key="virtualRow.data.key"
                :option="destinationEntity(virtualRow.data.item)"
                :kind="virtualRow.data.kind"
                :checked="isItemChecked(virtualRow.data.item)"
                :last-of-type="virtualRow.data.lastOfGroup"
                @toggle="emit('select', virtualRow.data.item)"
              />
            </template>
          </div>
        </div>
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .destination-dropdown {
    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    box-sizing: border-box;
    min-width: 420px;
    width: 100%;

    &__scroller {
      max-height: 380px;
      overflow-y: auto;
    }

    &__group-header {
      align-items: center;
      box-sizing: border-box;
      color: var(--color-text-primary);
      display: flex;
      height: var(--dropdown-header-row-height);
      padding: 0 var(--spacing-m);
    }

    &__loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--spacing-l) var(--spacing-m);
    }

    &__empty {
      color: var(--color-text-secondary);
      padding: var(--spacing-l) var(--spacing-m);
      text-align: center;
    }
  }
</style>
